import { useState } from "react";
import PageHero from "../components/PageHero";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { submitInquiry } from "../lib/api";
import { COMPANY } from "../lib/constants";

export default function ContactPage() {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    product_interest: "",
    message: "",
  });

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await submitInquiry({ ...form, source: "contact" });
      toast.success("Thank you! We'll be in touch within 24 hours.");
      setForm({ name: "", email: "", phone: "", company: "", product_interest: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try WhatsApp or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHero
        eyebrow="Contact"
        title="Let's start a conversation."
        description="Whether you have a detailed RFQ or a rough brief, we'd love to help. Reach out and one of our sourcing specialists will respond within 24 hours."
        image="https://images.pexels.com/photos/4065876/pexels-photo-4065876.jpeg?auto=compress&cs=tinysrgb&w=2400"
      />

      <section className="section-y">
        <div className="container-x grid md:grid-cols-12 gap-12">
          {/* Contact info */}
          <aside className="md:col-span-5 space-y-10">
            <div>
              <p className="eyebrow mb-3">Visit us</p>
              <h3 className="font-serif text-3xl mb-4">Our office</h3>
              <div className="space-y-4 text-brand-ink/85">
                <div className="flex gap-4">
                  <MapPin className="w-5 h-5 text-brand-terracotta shrink-0 mt-0.5" />
                  <span className="leading-relaxed">{COMPANY.address}</span>
                </div>
                <div className="flex gap-4">
                  <Clock className="w-5 h-5 text-brand-terracotta shrink-0 mt-0.5" />
                  <span>{COMPANY.hours}</span>
                </div>
              </div>
            </div>

            <div>
              <p className="eyebrow mb-3">Direct channels</p>
              <div className="space-y-3">
                <a
                  href={COMPANY.phoneHref}
                  data-testid="contact-phone-link"
                  className="flex items-center gap-4 p-4 border border-brand-line bg-brand-surface hover:border-brand-moss transition"
                >
                  <Phone className="w-5 h-5 text-brand-moss" />
                  <div>
                    <div className="text-xs uppercase tracking-wider text-brand-muted">Call us</div>
                    <div className="font-medium">{COMPANY.phone}</div>
                  </div>
                </a>
                <a
                  href={COMPANY.emailHref}
                  data-testid="contact-email-link"
                  className="flex items-center gap-4 p-4 border border-brand-line bg-brand-surface hover:border-brand-moss transition"
                >
                  <Mail className="w-5 h-5 text-brand-moss" />
                  <div>
                    <div className="text-xs uppercase tracking-wider text-brand-muted">Email us</div>
                    <div className="font-medium">{COMPANY.email}</div>
                  </div>
                </a>
              </div>
            </div>

            {/* Map placeholder */}
            <div className="aspect-[4/3] bg-brand-surfaceAlt relative overflow-hidden border border-brand-line">
              <iframe
                title="Goyam Ingredients Office — Nariman Point, Mumbai"
                src="https://www.openstreetmap.org/export/embed.html?bbox=72.8175%2C18.9230%2C72.8275%2C18.9330&layer=mapnik&marker=18.9280%2C72.8225"
                className="w-full h-full"
                style={{ border: 0 }}
                loading="lazy"
                data-testid="contact-map"
              />
            </div>
          </aside>

          {/* Form */}
          <div className="md:col-span-7">
            <div className="bg-brand-surface border border-brand-line p-8 md:p-12">
              <p className="eyebrow mb-3">General inquiry</p>
              <h2 className="font-serif text-3xl md:text-4xl leading-tight mb-8">
                Tell us what you're looking for.
              </h2>

              <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="c-name" className="text-xs tracking-wider uppercase">Full name*</Label>
                    <Input
                      id="c-name"
                      value={form.name}
                      onChange={update("name")}
                      data-testid="contact-input-name"
                      className="mt-1.5 bg-white border-brand-line rounded-sm py-6"
                    />
                  </div>
                  <div>
                    <Label htmlFor="c-email" className="text-xs tracking-wider uppercase">Work email*</Label>
                    <Input
                      id="c-email"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      data-testid="contact-input-email"
                      className="mt-1.5 bg-white border-brand-line rounded-sm py-6"
                    />
                  </div>
                  <div>
                    <Label htmlFor="c-phone" className="text-xs tracking-wider uppercase">Phone</Label>
                    <Input
                      id="c-phone"
                      value={form.phone}
                      onChange={update("phone")}
                      data-testid="contact-input-phone"
                      className="mt-1.5 bg-white border-brand-line rounded-sm py-6"
                    />
                  </div>
                  <div>
                    <Label htmlFor="c-company" className="text-xs tracking-wider uppercase">Company</Label>
                    <Input
                      id="c-company"
                      value={form.company}
                      onChange={update("company")}
                      data-testid="contact-input-company"
                      className="mt-1.5 bg-white border-brand-line rounded-sm py-6"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="c-product" className="text-xs tracking-wider uppercase">Product / category of interest</Label>
                  <Input
                    id="c-product"
                    value={form.product_interest}
                    onChange={update("product_interest")}
                    placeholder="e.g. Niacinamide, preservatives, essential oils"
                    data-testid="contact-input-product"
                    className="mt-1.5 bg-white border-brand-line rounded-sm py-6"
                  />
                </div>
                <div>
                  <Label htmlFor="c-msg" className="text-xs tracking-wider uppercase">Message*</Label>
                  <Textarea
                    id="c-msg"
                    value={form.message}
                    onChange={update("message")}
                    data-testid="contact-input-message"
                    placeholder="Tell us about your application, target quantity, and documentation needs."
                    className="mt-1.5 bg-white border-brand-line rounded-sm min-h-[150px]"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full disabled:opacity-60"
                  data-testid="contact-submit-btn"
                >
                  {loading ? "Sending…" : "Send Inquiry"}
                </button>
                <p className="text-xs text-brand-muted text-center">
                  By submitting this form, you agree to be contacted regarding your inquiry.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
